import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Alert, Skeleton } from '@material-ui/lab';
import {
  Box,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Button as MuiButton,
} from '@material-ui/core';
import {
  FiArchive,
  FiArrowDown,
  FiArrowUp,
  FiBox,
  FiCheckCircle,
  FiCheckSquare,
  FiClock,
  FiCornerDownRight,
  FiPackage,
  FiSearch,
  FiSend,
  FiX,
} from 'react-icons/fi';

import { getFilialUser } from '../../services/auth';
import { removeAccents } from '../../utils/functions';
import {
  STATUS_IDS,
  MATRIZ_FILIAL_CODE,
  isDivinopolisCep,
} from '../../constants/pedidos';

import editStatusOrder from '../../actions/pedidos/editar-status-pedido';
import editHoldOrder from '../../actions/pedidos/editar-hold-pedido';
import emitirEtiqueta from '../../actions/emitirEtiqueta';
import getListaPedidosByStatus from '../../actions/relatorios/lista-pedidos-by-status';
import getListaPedidosByStatus2 from '../../actions/relatorios/lista-pedidos-by-status-2';
import getListaPedidosSeparados from '../../actions/relatorios/lista-pedidos-separados';
import getListaPedidosAprovados from '../../actions/relatorios/lista-pedidos-aprovados';
import buscarFilialPorCodigoFilial from '../../actions/cadastro/buscar-filial-por-codigo-filial';

import { PedidoCard } from '../../components/Pedidos/PedidoCard';
import { PedidoDetalhesDialog } from '../../components/Pedidos/PedidoDetalhesDialog';

// Cor por etapa do fluxo — ajuda a localização visual ao percorrer o kanban.
const COLUMN_COLORS = {
  pendentes: '#ef6c00',  // laranja — atenção/conferência
  separados: '#1976d2',  // azul — em andamento
  aprovados: '#2e7d32',  // verde — pagamento aprovado
  expedicao: '#6a1b9a',  // roxo — logística interna
  retirada: '#00838f',   // ciano — interação com cliente
  backoffice: '#455a64', // azul-acinzentado — administrativo
  enviado: '#283593',    // indigo — em trânsito externo
};

const ColumnHeader = ({ icon: Icon, title, count, color, sortOrder, onToggleSort }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      px: 1,
      py: 0.75,
      borderRadius: '8px 8px 0 0',
      bgcolor: color,
      minWidth: 0,
    }}
  >
    <Icon size={14} color="#fff" style={{ flexShrink: 0 }} />
    <Typography
      sx={{
        color: '#fff',
        fontWeight: 600,
        fontSize: '0.72rem',
        lineHeight: 1.15,
        textAlign: 'center',
        flex: 1,
        minWidth: 0,
        wordBreak: 'break-word',
      }}
    >
      {title}
    </Typography>
    {count !== undefined && (
      <Box sx={{ bgcolor: 'rgba(255,255,255,0.25)', borderRadius: 0.75, px: 0.75, py: 0.1, flexShrink: 0 }}>
        <Typography sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 700 }}>{count}</Typography>
      </Box>
    )}
    <Tooltip
      title={
        sortOrder === 'asc'
          ? 'Nº crescente — clique para inverter'
          : sortOrder === 'desc'
          ? 'Nº decrescente — clique para limpar'
          : 'Ordenar por nº do pedido'
      }
    >
      <IconButton
        size="small"
        onClick={onToggleSort}
        sx={{
          p: 0.25,
          color: '#fff',
          bgcolor: sortOrder ? 'rgba(255,255,255,0.25)' : 'transparent',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
        }}
      >
        {sortOrder === 'desc' ? <FiArrowDown size={12} /> : <FiArrowUp size={12} />}
      </IconButton>
    </Tooltip>
  </Box>
);

const ColumnSkeleton = () => (
  <Stack spacing={0.75}>
    {[0, 1, 2].map((i) => (
      <Skeleton key={i} variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
    ))}
  </Stack>
);

const ColumnEmpty = ({ filtered }) => (
  <Box sx={{ textAlign: 'center', py: 3, color: 'text.disabled', fontSize: '0.72rem' }}>
    {filtered ? 'Nenhum resultado' : 'Nenhum pedido'}
  </Box>
);

const ColumnError = ({ onRetry }) => (
  <Alert
    severity="error"
    action={
      <MuiButton color="inherit" size="small" onClick={onRetry}>
        Tentar
      </MuiButton>
    }
  >
    Erro ao carregar
  </Alert>
);

const buildColumns = ({ onSeparar, onIniciarExpedicao, onColocarParaRetirada, onFinalizarRetirada, onColocarEmTransito, onFinalizarEnviado }) => [
  {
    key: 'pendentes',
    title: 'Pendentes/Conferência',
    icon: FiClock,
    fetcher: () =>
      getListaPedidosByStatus2({
        status1: STATUS_IDS.PAG_APROVADO,
        status2: STATUS_IDS.PENDENTE_PAGAMENTO,
        isHold: false,
        filialUsuario: getFilialUser(),
      }),
    getAction: (item) => ({ label: 'Separar', icon: <FiCheckSquare />, onClick: () => onSeparar(item) }),
  },
  {
    key: 'separados',
    title: 'Pedidos Separados',
    icon: FiPackage,
    fetcher: () =>
      getListaPedidosSeparados({
        status1: STATUS_IDS.PAG_APROVADO,
        status2: STATUS_IDS.PENDENTE_PAGAMENTO,
        isHold: true,
        filialUsuario: getFilialUser(),
      }),
    getAction: () => null,
  },
  {
    key: 'aprovados',
    title: 'Pag. Aprovados',
    icon: FiCheckCircle,
    fetcher: () =>
      getListaPedidosAprovados({
        status: STATUS_IDS.PAG_APROVADO,
        filialUsuario: getFilialUser(),
        isHold: true,
      }),
    getAction: (item) => ({ label: 'Expedir', icon: <FiCheckSquare />, onClick: () => onIniciarExpedicao(item) }),
  },
  {
    key: 'expedicao',
    title: 'Expedição',
    icon: FiBox,
    fetcher: () =>
      getListaPedidosByStatus({
        status: STATUS_IDS.EXPEDICAO,
        isHold: true,
        filialUsuario: getFilialUser(),
      }),
    getAction: (item) => ({ label: 'Disponibilizar', icon: <FiCheckSquare />, onClick: () => onColocarParaRetirada(item) }),
  },
  {
    key: 'retirada',
    title: 'Retirada/Coleta',
    icon: FiCornerDownRight,
    fetcher: () =>
      getListaPedidosByStatus2({
        status1: STATUS_IDS.RETIRADA_DISPONIVEL,
        status2: STATUS_IDS.COLETA,
        isHold: true,
        filialUsuario: getFilialUser(),
      }),
    getAction: (item) => ({ label: 'Finalizar', icon: <FiCheckSquare />, onClick: () => onFinalizarRetirada(item) }),
  },
  {
    key: 'backoffice',
    title: 'Backoffice',
    icon: FiArchive,
    matrizOnly: true,
    fetcher: () =>
      getListaPedidosByStatus({
        status: STATUS_IDS.BACKOFFICE,
        isHold: true,
        filialUsuario: getFilialUser(),
      }),
    getAction: (item) => ({ label: 'Em Trânsito', icon: <FiCheckSquare />, onClick: () => onColocarEmTransito(item) }),
  },
  {
    key: 'enviado',
    title: 'Enviado',
    icon: FiSend,
    matrizOnly: true,
    fetcher: () =>
      getListaPedidosByStatus({
        status: STATUS_IDS.EM_TRANSITO,
        isHold: true,
        filialUsuario: getFilialUser(),
      }),
    getAction: (item) => ({ label: 'Finalizar', icon: <FiCheckSquare />, onClick: () => onFinalizarEnviado(item) }),
  },
];

const buildEnderecoEtiqueta = (data) => ({
  logradouro: removeAccents(data.logradouro),
  numero: data.numero,
  bairro: removeAccents(data.bairro),
  localidade: removeAccents(data.localidade),
  complemento: removeAccents(data.complemento),
  uf: data.uf,
  cep: data.cep,
});

const matchesQuery = (item, query) => {
  if (!query) return true;
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const number = String(item.number ?? '').toLowerCase();
  const first = removeAccents(item.first_name ?? '').toLowerCase();
  const last = removeAccents(item.last_name ?? '').toLowerCase();
  const cpf = String(item.cpf ?? '').toLowerCase();
  return number.includes(q) || first.includes(q) || last.includes(q) || cpf.includes(q);
};

const sortByNumber = (items, order) => {
  if (!order) return items;
  const sorted = [...items].sort((a, b) => {
    const an = Number(a.number);
    const bn = Number(b.number);
    if (Number.isNaN(an) || Number.isNaN(bn)) {
      return String(a.number).localeCompare(String(b.number));
    }
    return an - bn;
  });
  return order === 'desc' ? sorted.reverse() : sorted;
};

// asc → desc → null → asc
const nextSortOrder = (current) => {
  if (current === 'asc') return 'desc';
  if (current === 'desc') return null;
  return 'asc';
};

function PedidosPage() {
  const [pedidos, setPedidos] = useState({
    pendentes: [],
    separados: [],
    aprovados: [],
    expedicao: [],
    retirada: [],
    backoffice: [],
    enviado: [],
  });
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [detalhesPedido, setDetalhesPedido] = useState(null);
  const [dadosFilial, setDadosFilial] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: 'info', message: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrders, setSortOrders] = useState({});

  const toggleSort = useCallback((columnKey) => {
    setSortOrders((prev) => {
      const next = nextSortOrder(prev[columnKey]);
      const updated = { ...prev };
      if (next) updated[columnKey] = next;
      else delete updated[columnKey];
      return updated;
    });
  }, []);

  const isMatriz = getFilialUser() === MATRIZ_FILIAL_CODE;

  const showToast = useCallback((severity, message) => {
    setToast({ open: true, severity, message });
  }, []);

  const editStatus = useCallback(async ({ statusId, item, isHold = true }) => {
    try {
      await editStatusOrder({ status_id: statusId, number: item.number, isHold });
      carregaLista();
    } catch (err) {
      console.error(err);
      showToast('error', 'Não foi possível atualizar o status do pedido.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  const editHold = useCallback(async ({ item, isHold = true }) => {
    try {
      await editHoldOrder({ number: item.number, isHold });
      carregaLista();
    } catch (err) {
      console.error(err);
      showToast('error', 'Não foi possível separar o pedido.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showToast]);

  const handleFinalizarRetirada = useCallback(
    (item) => {
      const nextStatus = isDivinopolisCep(item?.shipping_address?.postal_code)
        ? STATUS_IDS.BACKOFFICE
        : STATUS_IDS.FINALIZADO;
      editStatus({ statusId: nextStatus, item });
    },
    [editStatus],
  );

  const columns = useMemo(
    () =>
      buildColumns({
        onSeparar: (item) => editHold({ item, isHold: true }),
        onIniciarExpedicao: (item) => editStatus({ statusId: STATUS_IDS.EXPEDICAO, item }),
        onColocarParaRetirada: (item) => editStatus({ statusId: STATUS_IDS.RETIRADA_DISPONIVEL, item }),
        onFinalizarRetirada: handleFinalizarRetirada,
        onColocarEmTransito: (item) => editStatus({ statusId: STATUS_IDS.EM_TRANSITO, item }),
        onFinalizarEnviado: (item) => editStatus({ statusId: STATUS_IDS.FINALIZADO, item }),
      }),
    [editHold, editStatus, handleFinalizarRetirada],
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => !c.matrizOnly || isMatriz),
    [columns, isMatriz],
  );

  const fetchColumn = useCallback(async (column) => {
    try {
      const res = await column.fetcher();
      setPedidos((prev) => ({ ...prev, [column.key]: res.data ?? [] }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next[column.key];
        return next;
      });
    } catch (err) {
      console.error(`[PedidosPage] fetch ${column.key}`, err);
      setErrors((prev) => ({ ...prev, [column.key]: err }));
    }
  }, []);

  const carregaLista = useCallback(async () => {
    setLoading(true);
    await Promise.allSettled(visibleColumns.map((c) => fetchColumn(c)));
    setLoading(false);
  }, [fetchColumn, visibleColumns]);

  useEffect(() => {
    carregaLista();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verPedido = useCallback(async (item) => {
    setDetalhesPedido(item);
    setDadosFilial(null);
    setDialogOpen(true);
    try {
      const res = await buscarFilialPorCodigoFilial(item.dadosOrigemPedido.filial);
      setDadosFilial(res.data);
    } catch (err) {
      console.error('[PedidosPage] buscarFilialPorCodigoFilial', err);
      showToast('warning', 'Não foi possível carregar os dados da filial.');
    }
  }, [showToast]);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setDetalhesPedido(null);
    setDadosFilial(null);
  }, []);

  const handleEmitirEtiqueta = useCallback(async () => {
    if (!detalhesPedido) return;
    try {
      const [resFaturamento, resOrigem] = await Promise.all([
        buscarFilialPorCodigoFilial(detalhesPedido.items[0].filial),
        buscarFilialPorCodigoFilial(detalhesPedido.dadosOrigemPedido.filial),
      ]);

      const remetente = buildEnderecoEtiqueta(resFaturamento.data);
      const filialOrigem = {
        nome_completo: removeAccents(detalhesPedido.shipping_address?.full_name),
        ...buildEnderecoEtiqueta(resOrigem.data),
      };
      const enderecoCliente = {
        nome_completo: removeAccents(detalhesPedido.shipping_address?.full_name),
        logradouro: removeAccents(detalhesPedido.shipping_address?.address1),
        numero: detalhesPedido.shipping_address?.number,
        bairro: removeAccents(detalhesPedido.shipping_address?.address2),
        localidade: removeAccents(detalhesPedido.shipping_address?.city),
        complemento: removeAccents(detalhesPedido.shipping_address?.details),
        uf: detalhesPedido.shipping_address?.state,
        cep: detalhesPedido.shipping_address?.postal_code,
      };

      const dataPedido = {
        remetente,
        destinatario: detalhesPedido.tipoEntrega === 'Entrega' ? enderecoCliente : filialOrigem,
        pedido: {
          numero: detalhesPedido.number,
          filial: detalhesPedido.tipoEntrega === 'Retirada' ? detalhesPedido.dadosOrigemPedido.filial : '',
        },
      };

      await emitirEtiqueta(dataPedido);
      showToast('success', 'Etiqueta enviada para impressão.');
    } catch (err) {
      console.error('[PedidosPage] emitirEtiqueta', err);
      showToast('error', 'Não foi possível emitir a etiqueta.');
    }
  }, [detalhesPedido, showToast]);

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #e5e5e5', py: 1.5 }}>
        <Box
          sx={{
            maxWidth: '98%',
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#1a1a2e' }}>Pedidos</Typography>
          <TextField
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por nº, cliente ou CPF"
            size="small"
            variant="outlined"
            sx={{ width: { xs: '60%', sm: 360 }, bgcolor: '#fafafa' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch size={16} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="Limpar busca">
                    <FiX size={14} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </Box>

      <Box sx={{ maxWidth: '98%', mx: 'auto', py: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          {visibleColumns.map((col) => {
            const rawItems = pedidos[col.key] ?? [];
            const error = errors[col.key];
            const color = COLUMN_COLORS[col.key];
            const sortOrder = sortOrders[col.key];
            const items = sortByNumber(rawItems.filter((item) => matchesQuery(item, searchQuery)), sortOrder);
            return (
              <Box
                key={col.key}
                sx={{
                  flex: '1 1 0',
                  minWidth: 0,
                }}
              >
                <ColumnHeader
                  icon={col.icon}
                  title={col.title}
                  count={loading ? undefined : items.length}
                  color={color}
                  sortOrder={sortOrder}
                  onToggleSort={() => toggleSort(col.key)}
                />
                <Box sx={{ mt: 1 }}>
                  {loading ? (
                    <ColumnSkeleton />
                  ) : error ? (
                    <ColumnError onRetry={() => fetchColumn(col)} />
                  ) : items.length === 0 ? (
                    <ColumnEmpty filtered={Boolean(searchQuery && rawItems.length > 0)} />
                  ) : (
                    <Stack spacing={0.75}>
                      {items.map((item) => (
                        <PedidoCard
                          key={item._id}
                          item={item}
                          accentColor={color}
                          onClick={() => verPedido(item)}
                          action={col.getAction(item)}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <PedidoDetalhesDialog
        open={dialogOpen}
        pedido={detalhesPedido}
        dadosFilial={dadosFilial}
        onClose={handleCloseDialog}
        onEmitirEtiqueta={handleEmitirEtiqueta}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withRouter(PedidosPage);
